import type { ChatMessage, ChatSummary, OutputChunk } from "./types";

export const INITIAL_CONVERSATION: { hostId: string; messages: ChatMessage[] } = {
  hostId: "prod-db-replica",
  messages: [
    {
      id: "u1", role: "user", time: "14:38",
      text: "Replica is lagging — figure out why and bring it back in sync without nuking the slot.",
    },
    {
      id: "a1", role: "agent", time: "14:38",
      text: "I'll check replication state, walk the recent WAL, and look at network + load. Starting with a quick health pass.",
      tools: [
        {
          id: "t1",
          status: "success",
          icon: "terminal",
          title: { verb: "Ran", what: "psql replication health" },
          cmd: 'sudo -u postgres psql -c "select client_addr, state, sync_state, replay_lag from pg_stat_replication;"',
          output: [
            { c: "mut", t: " client_addr  |   state   | sync_state |  replay_lag\n" },
            { c: "mut", t: "--------------+-----------+------------+-------------\n" },
            { c: "",    t: " 10.0.4.22    | streaming | async      | " },
            { c: "yellow", t: "00:00:04.213\n" },
            { c: "mut", t: "(1 row)\n" },
          ],
        },
      ],
    },
    {
      id: "a2", role: "agent", time: "14:38",
      text: "Replay lag is ~4.2s and growing. Recv lag is fine — it's a replay bottleneck. Let me check if a long-running query is blocking apply, then look at IO.",
      tools: [
        {
          id: "t2",
          status: "success",
          icon: "cpu",
          title: { verb: "Ran", what: "replica wait events (1s sample)" },
          cmd: 'sudo -u postgres psql -c "select wait_event_type, wait_event, count(*) from pg_stat_activity where state != \'idle\' group by 1,2 order by 3 desc;"',
          output: [
            { c: "mut", t: " wait_event_type |    wait_event    | count\n" },
            { c: "mut", t: "-----------------+------------------+-------\n" },
            { c: "",    t: " Lock            | " }, { c: "red", t: "transactionid    " }, { c: "", t: " |     1\n" },
            { c: "",    t: " IO              | DataFileRead     |     1\n" },
            { c: "mut", t: "(2 rows)\n" },
          ],
        },
      ],
    },
    {
      id: "a3", role: "agent", time: "14:38",
      text: "There's a long analytic query holding a lock and blocking WAL replay (typical for read-replica conflict). I want to terminate it. This will cancel pid **39281** — analytic query, 6m runtime, low business value (from your runbook).",
      tools: [
        {
          id: "t3",
          status: "pending",
          icon: "shield",
          title: { verb: "Approve", what: "pg_terminate_backend(39281)" },
          cmd: 'sudo -u postgres psql -c "select pg_terminate_backend(39281);"',
          previewLines: [
            { c: "mut", t: "# pid    user      query_start  state               query\n" },
            { c: "",    t: "  39281  analytics 14:32:18     active(6m20s)  " }, { c: "acc", t: " select count(*) from events_2024 ...\n" },
          ],
          needsApproval: true,
        },
      ],
    },
  ],
};

export const TERMINAL_SESSIONS: Record<string, OutputChunk[]> = {
  "prod-db-replica": [
    { c: "mut", t: "# session opened — prod-db-replica  •  postgres@10.0.4.22  •  ssh-key Ed25519\n" },
    { c: "mut", t: "# 64-bit Debian 12, kernel 6.1.0-21, uptime 41 days\n" },
    { c: "",    t: "\n" },
    { c: "green", t: "postgres" }, { c: "mut", t: "@" }, { c: "acc", t: "prod-db-replica" }, { c: "mut", t: ":" }, { c: "acc2", t: "~" }, { c: "", t: "$ " }, { c: "white", t: "systemctl status postgresql@15-main --no-pager\n" },
    { c: "mut", t: "● postgresql@15-main.service - PostgreSQL Cluster 15-main\n" },
    { c: "mut", t: "     Loaded: loaded (/lib/systemd/system/postgresql@.service; enabled)\n" },
    { c: "",    t: "     Active: " }, { c: "green", t: "active (running)" }, { c: "mut", t: " since Mon 2026-05-19 02:11 UTC; 2 days ago\n" },
    { c: "mut", t: "   Main PID: 1287 (postgres)\n" },
    { c: "mut", t: "      Tasks: 24 (limit: 4683)\n" },
    { c: "",    t: "\n" },
    { c: "green", t: "postgres" }, { c: "mut", t: "@" }, { c: "acc", t: "prod-db-replica" }, { c: "mut", t: ":" }, { c: "acc2", t: "~" }, { c: "", t: "$ " }, { c: "white", t: "tail -n 4 /var/log/postgresql/postgresql-15-main.log\n" },
    { c: "mut", t: "2026-05-21 14:36:52 UTC [1287] LOG:  recovery restartpoint complete\n" },
    { c: "mut", t: "2026-05-21 14:37:14 UTC [1287] LOG:  WAL receiver: 0/3FB12A8 received\n" },
    { c: "yellow", t: "2026-05-21 14:38:02 UTC [1287] WARNING:  recovery still waiting on backend pid 39281 (4.2s)\n" },
    { c: "mut", t: "2026-05-21 14:38:09 UTC [1287] LOG:  streaming connected\n" },
    { c: "",    t: "\n" },
  ],
};

export const CHATS: Record<string, ChatSummary[]> = {
  "prod-api-1": [
    { id: "c-1", title: "Diagnose 502s from nginx", time: "12m", preview: "Sia: I checked nginx + upstream — looks like…", unread: 1 },
    { id: "c-2", title: "Roll out v2.41.3", time: "2h", preview: "You: ship it", unread: 0 },
    { id: "c-3", title: "TLS cert renewal", time: "1d", preview: "Sia: certbot renewed for api.cotty…", unread: 0 },
  ],
  "prod-api-2": [
    { id: "c-1", title: "Health check setup", time: "34m", preview: "Sia: Configured /healthz to return…", unread: 0 },
    { id: "c-2", title: "Memory leak in worker", time: "yesterday", preview: "You: still climbing after restart", unread: 0 },
  ],
  "prod-db-1": [
    { id: "c-1", title: "Vacuum tuning for events table", time: "1h", preview: "Sia: autovacuum_vacuum_scale_factor lowered…", unread: 0 },
    { id: "c-2", title: "Backup verification", time: "3h", preview: "Sia: pg_basebackup integrity check passed", unread: 0 },
    { id: "c-3", title: "Add read replica", time: "2d", preview: "You: how do we add another replica?", unread: 0 },
  ],
  "prod-db-replica": [
    { id: "c-1", title: "Replication lag triage", time: "now", preview: "Sia: replay lag is ~4.2s and growing…", unread: 2 },
    { id: "c-2", title: "Slot cleanup", time: "18m", preview: "Sia: 2 stale slots dropped", unread: 0 },
    { id: "c-3", title: "Failover drill", time: "yesterday", preview: "You: pause WAL apply for 30s", unread: 0 },
    { id: "c-4", title: "Disk space on /var", time: "2d", preview: "Sia: /var sitting at 49%, projected…", unread: 0 },
  ],
  "edge-eu": [
    { id: "c-1", title: "Geo-routing rules", time: "4h", preview: "Sia: added EU-only rule for /pay…", unread: 0 },
  ],
  "edge-ap": [
    { id: "c-1", title: "SSH connection lost", time: "14m", preview: "Sia: cannot reach :22 — checking…", unread: 1 },
  ],
  "staging-api": [
    { id: "c-1", title: "Smoke tests after deploy", time: "1m", preview: "Sia: all 42 endpoints returning 200", unread: 0 },
    { id: "c-2", title: "Seed test data", time: "2h", preview: "You: load the demo fixtures", unread: 0 },
  ],
  "staging-worker": [
    { id: "c-1", title: "Queue worker spike", time: "8m", preview: "Sia: scaling concurrency 4 → 8", unread: 0 },
  ],
  "jumpbox": [
    { id: "c-1", title: "Add new SSH key for Priya", time: "5h", preview: "Sia: key authorized for ops group", unread: 0 },
    { id: "c-2", title: "fail2ban tuning", time: "1d", preview: "Sia: bantime raised to 24h", unread: 0 },
  ],
};
